// /pages/BadgesPage.jsx
// JG Trailblazer — Player Badges (list) page with BadgeGallery parity
// Supports /badges (self) and /p/:playerId/badges (handle OR id)

import React, {
  useMemo,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useParams, Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import usePlayerBadges from '../../hooks/usePlayerBadges';
import useResolvePlayerMongoId from '../../hooks/useResolvePlayerModel';
import { useTheme } from '../../context/ThemeContext';
import SmartImage from '../../components/common/SmartImage';
import ShareBadgeAsPost from '../../components/share/ShareBadgeAsPost'; // NEW
import axiosConn from '../../axiosConn'; // NEW: for owner meta fetch

/** Dark pref like BadgeGallery */
// Theme-aware dark mode (uses ThemeContext first, falls back to system)
const useDark = () => {
  const { darkTheme } = useTheme?.() || {};
  const [prefers, setPrefers] = useState(
    window.matchMedia?.('(prefers-color-scheme: dark)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    const onChange = (e) => setPrefers(e.matches);
    mq?.addEventListener?.('change', onChange);
    return () => mq?.removeEventListener?.('change', onChange);
  }, []);

  return typeof darkTheme === 'boolean' ? darkTheme : prefers;
};

/** Tiny analytics shim */
function useAnalytics() {
  const fired = useRef(new Set());
  const track = useCallback((name, payload = {}) => {
    try {
      if (window.dataLayer) window.dataLayer.push({ event: name, ...payload });
      if (window.rudderanalytics?.track)
        window.rudderanalytics.track(name, payload);
    } catch {}
    const sig = `${name}:${JSON.stringify(payload)}`;
    if (!fired.current.has(sig)) {
      console.log('[analytics]', name, payload);
      fired.current.add(sig);
    }
  }, []);
  return { track };
}

/** Clipboard helper */
async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  }
}

const ensureKeyframes = (() => {
  let done = false;
  return () => {
    if (done) return;
    done = true;
    const style = document.createElement('style');
    style.innerHTML = `
      .badgePageCard:hover,
      .badgePageCard:focus-visible {
        transform: translateY(-5px);
        box-shadow: 0 10px 28px -10px rgba(0,0,0,.35),0 6px 16px -8px rgba(0,0,0,.32);
        border-color: rgba(99,102,241,.55);
      }
      .badgePageCard:focus-visible {
        outline: 2px solid #6366f1;
        outline-offset: 4px;
      }
      @media (prefers-reduced-motion: reduce) {
        .badgePageCard { transition: none !important; }
      }
    `;
    document.head.appendChild(style);
  };
})();

const ensureBadgePageStyles = (() => {
  let done = false;
  return () => {
    if (done) return;
    done = true;
    const style = document.createElement('style');
    style.setAttribute('data-badges-page-overrides', 'true');
    style.innerHTML = `
      .badgePageCard { background: rgba(255,255,255,0.07); backdrop-filter: blur(4px); }
      .dark-theme .badgePageCard { background: rgba(255,255,255,0.07) !important; }
      :root:not(.dark-theme) .badgePageCard { background: #ffffff !important; }
      :root:not(.dark-theme) .badgePageCard:hover,
      :root:not(.dark-theme) .badgePageCard:focus-visible { background: #f8fafc !important; }
    `;
    document.head.appendChild(style);
  };
})();

const ensureNativeSelectLegible = (() => {
  let done = false;
  return () => {
    if (done) return;
    done = true;
    const style = document.createElement('style');
    style.setAttribute('data-native-select-legible', 'true');
    style.innerHTML = `
      select option { color: #111827 !important; }
      .dark-theme select { color: #f1f5f9 !important; }
    `;
    document.head.appendChild(style);
  };
})();

/* Utilities */
const toNum = (v) => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const cleaned = v
      .trim()
      .replace(/,/g, '')
      .replace(/[^0-9.+-]/g, '');
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const progressOf = (b) => {
  let p =
    toNum(b?.progressPercent) ||
    toNum(b?.progress?.percent) ||
    toNum(b?.progress?.percentage) ||
    toNum(b?.percentage) ||
    toNum(b?.percent);
  if (!p) {
    const cur = toNum(
      b?.progress?.current ?? b?.progress?.value ?? b?.current ?? b?.value,
    );
    const tgt = toNum(
      b?.progress?.target ?? b?.progress?.max ?? b?.target ?? b?.max,
    );
    if (tgt > 0) p = Math.round((cur / tgt) * 100);
  }
  return Math.max(0, Math.min(100, Math.round(p)));
};

const hasEarnedFlag = (b) => {
  const r = b?.raw || {};
  const cand = [
    b?.earned,
    b?.unlocked,
    b?.complete,
    b?.completed,
    b?.isEarned,
    b?.isComplete,
    b?.isCompleted,
    b?.claimed,
    b?.collected,
    b?.achieved,
    b?.status,
    r.earned,
    r.unlocked,
    r.complete,
    r.completed,
    r.isEarned,
    r.isComplete,
    r.isCompleted,
    r.claimed,
    r.collected,
    r.achieved,
    r.status,
  ];
  if (cand.some((v) => v === true)) return true;
  const s = String(cand.find((v) => typeof v === 'string') || '').toLowerCase();
  return [
    'earned',
    'complete',
    'completed',
    'unlocked',
    'claimed',
    'collected',
    'achieved',
    'done',
    'finished',
  ].includes(s);
};

const isEarnedLike = (b) =>
  !!b?.earnedAt || hasEarnedFlag(b) || progressOf(b) >= 100;

const lower = (s) => String(s || '').toLowerCase();
const normalizeName = (s) =>
  lower(s)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const isDailyVisit = (b) => {
  const metric = lower(b?.metric);
  if (metric === 'dailyvisit') return true; // NEW: use normalized metric
  const s = `${lower(b?.slug)} ${lower(b?.name)} ${lower(b?.category)} ${lower(
    b?.type,
  )}`;
  return /daily[-\s_]?visit|visit[-\s_]?daily|daily[-\s_]?login|login[-\s_]?daily/.test(
    s,
  );
};

const extractVisitDay = (b) => {
  if (typeof b?.threshold === 'number') return b.threshold; // NEW
  if (typeof b?.day === 'number') return b.day;
  if (typeof b?.streakDay === 'number') return b.streakDay;
  if (typeof b?.tier === 'number') return b.tier;
  if (typeof b?.level === 'number') return b.level;
  const cur = b?.progress?.current;
  if (typeof cur === 'number') return cur; // NEW fallback
  const s = `${lower(b?.slug)} ${lower(b?.name)}`;
  const m =
    s.match(/(?:^|[\s_-])day\s*(\d{1,3})\b/) ||
    s.match(/(?:^|[\s_-])(\d{1,3})\s*day\b/) ||
    s.match(/(?:^|[\s_-])(\d{1,3})\b$/);
  return m ? Number(m[1]) : null;
};

const iconFingerprint = (url) => {
  try {
    const u = new URL(String(url), window.location.origin);
    const parts = u.pathname.split('/').filter(Boolean);
    const clean = parts.filter((p) => !/^v\d{3,}$/.test(p));
    return `${u.hostname}/${clean.slice(-3).join('/')}`.toLowerCase();
  } catch {
    return '';
  }
};

const dedupeKeyStrict = (b) => {
  const product = lower(b?.product || b?.raw?.product || '');
  const slug = lower(b?.slug);
  const base =
    b?.badgeId ||
    (slug ? `${slug}|${product}` : null) ||
    b?.id ||
    b?._id ||
    null;
  const day = isDailyVisit(b) ? extractVisitDay(b) : null;
  const tier = b?.tier && Number.isFinite(+b.tier) ? +b.tier : null;
  if (!base) return null;
  return `${base}|d:${day ?? ''}|t:${tier ?? ''}`;
};

const dedupeKeyFuzzy = (b) => {
  const product = lower(b?.product || b?.raw?.product || '');
  const name = normalizeName(b?.name || b?.slug || '');
  const day = isDailyVisit(b) ? extractVisitDay(b) : null;
  const icon = iconFingerprint(b?.iconUrl);
  const metric = lower(b?.metric || '');
  const thr = typeof b?.threshold === 'number' ? b.threshold : '';
  return `${name}|${product}|m:${metric}|t:${thr}|d:${day ?? ''}|i:${icon}`;
};

function betterOf(a, b) {
  const aEarn = isEarnedLike(a);
  const bEarn = isEarnedLike(b);
  if (aEarn !== bEarn) return aEarn ? a : b;
  const at = a?.earnedAt ? new Date(a.earnedAt).getTime() : 0;
  const bt = b?.earnedAt ? new Date(b.earnedAt).getTime() : 0;
  if (at !== bt) return at > bt ? a : b;
  const ap = progressOf(a);
  const bp = progressOf(b);
  if (ap !== bp) return ap > bp ? a : b;
  if (!!a?.slug !== !!b?.slug) return a?.slug ? a : b;
  return a;
}

function dedupeBadges(list) {
  const strict = new Map();
  const leftovers = [];
  for (const item of list || []) {
    const k = dedupeKeyStrict(item);
    if (k) {
      const prev = strict.get(k);
      strict.set(k, prev ? betterOf(prev, item) : item);
    } else leftovers.push(item);
  }
  const pass1 = [...strict.values(), ...leftovers];
  const fuzzy = new Map();
  for (const item of pass1) {
    const k = dedupeKeyFuzzy(item);
    const prev = fuzzy.get(k);
    fuzzy.set(k, prev ? betterOf(prev, item) : item);
  }
  return [...fuzzy.values()];
}

// NEW: deduper with debug logs (no behavior change unless dbg flag present)
function dedupeBadgesWithLogs(list) {
  const info = (b) => ({
    id: b?.id || b?._id || b?.badgeId || null,
    slug: b?.slug || null,
    name: b?.name || '',
    metric: b?.metric || '',
    threshold: b?.threshold,
    product: b?.product || b?.raw?.product || '',
    day: isDailyVisit(b) ? extractVisitDay(b) : null,
    earnedAt: b?.earnedAt || null,
    percent: progressOf(b),
    icon: b?.iconUrl,
  });

  const strict = new Map();
  const dropsStrict = [];
  const leftovers = [];

  for (const item of list || []) {
    const k = dedupeKeyStrict(item);
    if (k) {
      const prev = strict.get(k);
      if (prev) {
        const chosen = betterOf(prev, item);
        const dropped = chosen === prev ? item : prev;
        dropsStrict.push({
          key: k,
          chosen: info(chosen),
          dropped: info(dropped),
        });
        strict.set(k, chosen);
      } else {
        strict.set(k, item);
      }
    } else {
      leftovers.push(item);
    }
  }
  const pass1 = [...strict.values(), ...leftovers];

  const fuzzy = new Map();
  const dropsFuzzy = [];
  for (const item of pass1) {
    const k = dedupeKeyFuzzy(item);
    const prev = fuzzy.get(k);
    if (prev) {
      const chosen = betterOf(prev, item);
      const dropped = chosen === prev ? item : prev;
      dropsFuzzy.push({ key: k, chosen: info(chosen), dropped: info(dropped) });
      fuzzy.set(k, chosen);
    } else {
      fuzzy.set(k, item);
    }
  }

  console.groupCollapsed('[BadgesPage] Dedupe debug');
  console.log('Input count:', (list || []).length);
  console.log(
    'After strict pass:',
    pass1.length,
    'drops:',
    dropsStrict.length,
    dropsStrict,
  );
  console.log(
    'After fuzzy pass:',
    fuzzy.size,
    'drops:',
    dropsFuzzy.length,
    dropsFuzzy,
  );
  const dvDrops = [...dropsStrict, ...dropsFuzzy].filter(
    (d) =>
      /dailyvisit/i.test(String(d.chosen.metric)) ||
      /dailyvisit/i.test(String(d.dropped.metric)) ||
      /daily[-\s_]?visit|visit[-\s_]?daily|daily[-\s_]?login|login[-\s_]?daily/i.test(
        `${d.chosen.slug || ''} ${d.chosen.name || ''} ${
          d.dropped.slug || ''
        } ${d.dropped.name || ''}`,
      ),
  );
  if (dvDrops.length) {
    console.warn('[BadgesPage] Daily-visit dedupe drops:', dvDrops);
  }
  console.groupEnd();

  return [...fuzzy.values()];
}

export default function BadgesPage() {
  const { player } = useContext(AuthContext);
  const params = useParams();
  const dark = useDark();

  const viewerMongoId = player?.data?.player?._id || player?._id || null;
  const viewerHandle = player?.data?.player?.playerId || '';

  const hasScopedParam = Boolean(params.playerId);

  const { mongoId, handle, loading, error } = useResolvePlayerMongoId(
    hasScopedParam ? params.playerId : viewerHandle,
  );

  const norm = (s) => (s || '').toString().trim().toLowerCase();
  // If no :playerId in route => /badges (my badges) => self.
  // If :playerId exists => compare handle in URL to viewerHandle.
  const isSelf =
    !hasScopedParam || norm(params.playerId) === norm(viewerHandle);

  useEffect(() => {
    ensureKeyframes();
    ensureBadgePageStyles();
    ensureNativeSelectLegible();
  }, []);

  if (hasScopedParam) {
    if (loading) {
      return (
        <main
          style={{
            padding: '18px clamp(10px,3vw,28px) 30px',
            maxWidth: 1200,
            margin: '0 auto',
          }}
        >
          <h1 style={titleH1(dark)}>Badges</h1>
          <div style={{ fontSize: 14, color: dark ? '#94a3b8' : '#64748b' }}>
            Resolving player…
          </div>
        </main>
      );
    }
    if (!mongoId) {
      return (
        <main
          style={{
            padding: '18px clamp(10px,3vw,28px) 30px',
            maxWidth: 1200,
            margin: '0 auto',
          }}
        >
          <h1 style={titleH1(dark)}>Badges</h1>
          <div style={{ fontSize: 14, color: dark ? '#fca5a5' : '#b91c1c' }}>
            {error ? String(error) : 'Player not found.'}
          </div>
          <div style={{ marginTop: 12 }}>
            <Link to="/badges" style={pillBtn(colorTokens(dark))}>
              ← My badges
            </Link>
          </div>
        </main>
      );
    }
    return (
      <BadgesPageInner
        mongoId={mongoId}
        prettyId={handle || params.playerId}
        isSelf={mongoId === viewerMongoId}
      />
    );
  }

  if (!viewerMongoId) {
    return (
      <main
        style={{
          padding: '18px clamp(10px,3vw,28px) 30px',
          maxWidth: 1200,
          margin: '0 auto',
        }}
      >
        <h1 style={titleH1(dark)}>Badges</h1>
        <div style={{ fontSize: 14, color: dark ? '#94a3b8' : '#64748b' }}>
          Please sign in.
        </div>
      </main>
    );
  }

  return (
    <BadgesPageInner mongoId={viewerMongoId} prettyId={viewerHandle} isSelf />
  );
}

function BadgesPageInner({ mongoId, prettyId, isSelf }) {
  const { player } = useContext(AuthContext);
  const token = player?.token || null;
  const dark = useDark();
  const { track } = useAnalytics();

  const { badges, meta, status, error, refetch, refreshing } = usePlayerBadges(
    mongoId,
    {
      token,
      includeRarity: true,
      includeLocked: true,
      product: '',
      types: '',
      categories: '',
      limit: 1000,
      debug: true,
      dedupeStrategy: 'badgeId',
    },
  );

  useEffect(() => {
    track('BadgesPage Viewed', { mongoId, prettyId, isSelf });
  }, [mongoId, prettyId, isSelf, track]);

  const colors = colorTokens(dark);

  // NEW: Owner meta for the responsive header (avatar + handle)
  const [ownerMeta, setOwnerMeta] = useState({
    loading: true,
    data: null,
    error: null,
  });
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!mongoId) return;
      try {
        setOwnerMeta((s) => ({ ...s, loading: true, error: null }));
        const resp = await axiosConn.get(`players/${mongoId}`, {
          validateStatus: () => true,
        });
        if (!alive) return;
        if (resp.status >= 200 && resp.status < 300) {
          const p =
            resp.data?.player || resp.data?.data?.player || resp.data || null;
          setOwnerMeta({ loading: false, data: p, error: null });
        } else {
          setOwnerMeta({
            loading: false,
            data: null,
            error: resp?.data?.message || `HTTP ${resp.status}`,
          });
        }
      } catch (e) {
        if (!alive) return;
        setOwnerMeta({
          loading: false,
          data: null,
          error: e?.message || 'Failed to load owner',
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, [mongoId]);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('newest');

  const prepared = useMemo(() => {
    const list = Array.isArray(badges) ? badges : [];

    // NEW: enable logs with ?dbg=dedupe or ?dbg=badges
    let dbg = false;
    try {
      const qs = new URLSearchParams(window.location.search || '');
      const d = (qs.get('dbg') || '').toLowerCase();
      dbg = d.includes('dedupe') || d.includes('badges');
    } catch {}

    if (dbg) {
      console.groupCollapsed('[BadgesPage] Pre-dedupe list');
      console.log(
        list.map((b) => ({
          id: b?.id || b?._id || b?.badgeId || null,
          slug: b?.slug || null,
          name: b?.name,
          metric: b?.metric,
          threshold: b?.threshold,
          day: isDailyVisit(b) ? extractVisitDay(b) : null,
          earnedAt: b?.earnedAt || null,
          percent: progressOf(b),
        })),
      );
      console.groupEnd();
    }

    const deduped = dbg ? dedupeBadgesWithLogs(list) : dedupeBadges(list);

    if (dbg) {
      console.log('[BadgesPage] Post-dedupe count:', deduped.length);
    }

    if (sort === 'newest') {
      deduped.sort((a, b) => {
        const ad = a.earnedAt ? new Date(a.earnedAt).getTime() : 0;
        const bd = b.earnedAt ? new Date(b.earnedAt).getTime() : 0;
        return bd - ad;
      });
    } else if (sort === 'rarest') {
      deduped.sort((a, b) => {
        const ar =
          a.rarityScore ??
          a.rarity ??
          (a.globalEarnCount ? -a.globalEarnCount : 0) ??
          0;
        const br =
          b.rarityScore ??
          b.rarity ??
          (b.globalEarnCount ? -b.globalEarnCount : 0) ??
          0;
        if (ar !== br) return br - ar;
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
    } else {
      deduped.sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || '')),
      );
    }
    return deduped;
  }, [badges, sort]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = prepared.filter((b) => {
      const earned = isEarnedLike(b);
      const progress = progressOf(b);
      const locked = !earned && progress <= 0;
      if (filter === 'earned' && !earned) return false;
      if (filter === 'inProgress' && !(progress > 0 && !earned)) return false;
      if (filter === 'locked' && !locked) return false;
      return true;
    });
    if (q) {
      out = out.filter((b) =>
        `${b.name || ''} ${b.desc || b.description || ''} ${b.slug || ''}`
          .toLowerCase()
          .includes(q),
      );
    }
    return out;
  }, [prepared, filter, search]);

  useEffect(() => {
    if (filter !== 'earned') return;
    try {
      const getKey = (b) => b?.id || b?._id || b?.badgeId || b?.slug || '';
      const toInfo = (b) => ({
        key: getKey(b),
        slug: b?.slug || null,
        name: b?.name || '',
        product: b?.product || b?.raw?.product || '',
        progress: progressOf(b),
        earnedAt: b?.earnedAt || null,
      });
      const shouldBeListed = prepared.filter(
        (b) => isDailyVisit(b) && isEarnedLike(b),
      );
      const actuallyListed = filtered.filter((b) => isDailyVisit(b));
      const shouldKeys = new Set(shouldBeListed.map(getKey));
      const actualKeys = new Set(actuallyListed.map(getKey));
      const missing = shouldBeListed.filter((b) => !actualKeys.has(getKey(b)));
      const unexpected = actuallyListed.filter(
        (b) => !shouldKeys.has(getKey(b)),
      );
      console.groupCollapsed(
        '[BadgesPage] Earned — Daily Visit badges debug (should vs actual)',
      );
      console.log('Should be listed (daily-visit + earned-like):', {
        count: shouldBeListed.length,
        items: shouldBeListed.map(toInfo),
      });
      console.log('Actually listed (filtered Earned view):', {
        count: actuallyListed.length,
        items: actuallyListed.map(toInfo),
      });
      if (missing.length) {
        console.warn('Missing (should but not in list):', {
          count: missing.length,
          items: missing.map(toInfo),
        });
      }
      if (unexpected.length) {
        console.warn('Unexpected (in list but not earned-like):', {
          count: unexpected.length,
          items: unexpected.map(toInfo),
        });
      }
      console.groupEnd();
    } catch (e) {
      console.warn('[BadgesPage] Earned daily-visit debug failed:', e);
    }
  }, [filter, prepared, filtered]);

  const shareBadge = async (badge) => {
    const base = window.location.origin || 'https://www.JoeGalaxy.NET';
    const link = `${base}/p/${encodeURIComponent(
      prettyId,
    )}/badges/${encodeURIComponent(badge.slug || badge.id || badge._id)}`;
    const payload = {
      title: `My ${badge.name} badge`,
      text: badge.desc || badge.description || 'Check out this badge!',
      url: link,
    };
    track('Badge Share Clicked', {
      badgeId: badge.id || badge._id,
      slug: badge.slug || null,
      owner: prettyId,
    });
    if (navigator.share) {
      try {
        await navigator.share(payload);
        return;
      } catch {}
    }
    if (await copyText(link)) alert('Link copied to clipboard');
    else prompt('Copy this link', link);
  };

  const isLoading = status === 'loading';
  const softError = error && /not modified/i.test(error) ? null : error || null;

  // Owner header computed values
  const totalCount =
    meta?.earnedCount ??
    meta?.normalizedCount ??
    (Array.isArray(badges) ? badges.length : 0);
  const ownerHandle =
    ownerMeta.data?.playerId || ownerMeta.data?.handle || prettyId || '';
  const ownerAvatar =
    ownerMeta.data?.profileImg ||
    ownerMeta.data?.profileImage ||
    ownerMeta.data?.profilePhoto ||
    ownerMeta.data?.avatar ||
    ownerMeta.data?.photo ||
    ownerMeta.data?.image ||
    ownerMeta.data?.picture ||
    '';

  return (
    <main
      style={{
        padding: '18px clamp(10px,3vw,28px) 30px',
        maxWidth: 1200,
        margin: '0 auto',
      }}
      aria-labelledby="badges-title"
    >
      <header
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          marginBottom: 12,
        }}
      >
        <h1 id="badges-title" style={titleH1(dark)}>
          Badges
        </h1>

        {/* Owner header — compact, responsive, and explicit */}
        <div
          role="region"
          aria-label="Owner info"
          style={{
            flex: '1 1 100%',
            border: `1px solid ${colors.border}`,
            borderRadius: 18,
            background: colors.chipBg,
            padding: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              overflow: 'hidden',
              flex: '0 0 auto',
              boxShadow: dark
                ? '0 0 0 1px rgba(255,255,255,0.08)'
                : '0 0 0 1px #e5e7eb',
            }}
            aria-hidden="true"
          >
            <SmartImage
              src={ownerAvatar}
              alt={`${ownerHandle} avatar`}
              size={56}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}
          >
            <div
              style={{
                fontWeight: 800,
                letterSpacing: '.4px',
                lineHeight: 1.15,
                fontSize: 16,
                color: colors.text,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={ownerHandle}
            >
              {ownerMeta.loading ? 'Loading…' : ownerHandle}
            </div>
            <div
              style={{
                fontSize: 12,
                color: colors.textSubFaint,
                letterSpacing: '.35px',
              }}
            >
              {isSelf ? 'Your badges' : `Viewing ${ownerHandle}’s badges`}
              {typeof totalCount === 'number' ? ` • ${totalCount}` : ''}
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Link
              to={`/profile/${encodeURIComponent(ownerHandle)}`}
              style={{ ...pillBtn(colors), textDecoration: 'none' }}
              aria-label={`Open ${ownerHandle}'s profile`}
            >
              View profile
            </Link>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <input
            aria-label="Search badges"
            placeholder="Search badges"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={inputStyle(colors)}
          />

          <div
            role="group"
            aria-label="Filter badges"
            style={{ display: 'flex', gap: 6 }}
          >
            {[
              { key: 'all', label: 'All' },
              { key: 'earned', label: 'Earned' },
              { key: 'inProgress', label: 'In progress' },
              { key: 'locked', label: 'Locked' },
            ].map(({ key, label }) => (
              <button
                key={key}
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
                style={toggleBtn(filter === key, colors)}
                title={label}
              >
                {label}
              </button>
            ))}
          </div>

          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12,
              color: colors.textSubFaint,
            }}
          >
            <span style={{ fontSize: 12 }}>Sort</span>
            <select
              aria-label="Sort badges"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              style={selectStyle(colors)}
            >
              <option value="newest">Newest</option>
              <option value="rarest">Rarest</option>
              <option value="name">Name</option>
            </select>
          </label>

          <Link
            to={
              isSelf ? '/badges' : `/p/${encodeURIComponent(prettyId)}/badges`
            }
            style={{ ...pillBtn(colors), textDecoration: 'none' }}
          >
            ← Back
          </Link>

          <button
            onClick={() => refetch()}
            disabled={refreshing}
            style={pillBtn(colors)}
            aria-label="Refresh badges"
            title="Refresh badges"
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </header>

      {isLoading && (
        <div
          style={{
            fontSize: 14,
            color: colors.textSubFaint,
            padding: '8px 2px',
          }}
        >
          Loading badges…
        </div>
      )}

      {softError && (
        <div
          style={{
            color: dark ? '#fca5a5' : '#b91c1c',
            fontSize: 13,
            marginBottom: 8,
          }}
        >
          {String(softError)}
        </div>
      )}

      <section
        aria-live="polite"
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(auto-fill,minmax(clamp(110px,26vw,160px),1fr))',
          gap: 20,
        }}
      >
        {filtered.map((b) => {
          const earned = isEarnedLike(b);
          const progressPct = progressOf(b);
          const locked = !earned && progressPct <= 0;
          const earnedDate = b.earnedAt
            ? new Date(b.earnedAt).toLocaleDateString()
            : null;

          const detailLink = `/p/${encodeURIComponent(
            prettyId,
          )}/badges/${encodeURIComponent(b.slug || b.id || b._id)}`;

          return (
            <article
              key={b.id || b._id || b.slug}
              tabIndex={0}
              aria-label={`${b.name}${
                earnedDate ? `, earned ${earnedDate}` : ''
              }`}
              className="badgePageCard"
              style={badgeCard(colors, dark)}
              onFocus={() =>
                track('Badge Card Focused', { badgeId: b.id || b._id })
              }
              onMouseEnter={() =>
                track('Badge Card Hovered', { badgeId: b.id || b._id })
              }
            >
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <div style={badgeImageWrap(colors, dark)}>
                  <SmartImage
                    src={b.iconUrl}
                    alt={b.name}
                    loading="lazy"
                    size={88}
                    style={squareImg}
                  />
                  {!earned && (
                    <div
                      title={`${progressPct}% toward unlock`}
                      style={progressPill(colors)}
                    >
                      {progressPct}%
                    </div>
                  )}
                </div>
              </div>

              <div style={{ textAlign: 'center', padding: '0 6px' }}>
                <div style={badgeName(colors)}>{b.name}</div>
                {earnedDate || earned ? (
                  <div style={badgeMeta(colors)}>
                    {earnedDate ? `Earned: ${earnedDate}` : 'Earned'}
                  </div>
                ) : locked ? (
                  <div style={badgeMeta(colors)}>Locked</div>
                ) : (
                  <div style={badgeMeta(colors)}>Progress: {progressPct}%</div>
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  justifyContent: 'center',
                  marginTop: 14,
                  flexWrap: 'wrap',
                }}
              >
                <Link
                  to={detailLink}
                  onClick={() =>
                    track('Badge View Clicked', { badgeId: b.id || b._id })
                  }
                  style={{ ...pillBtn(colors), textDecoration: 'none' }}
                  aria-label={`View ${b.name}`}
                >
                  View
                </Link>
                <button
                  onClick={() => shareBadge(b)}
                  style={pillBtn(colors)}
                  aria-label={`Share ${b.name}`}
                  title="Share"
                >
                  Share
                </button>
                {isSelf && <ShareBadgeAsPost badge={b} />} {/* NEW pill */}
              </div>
            </article>
          );
        })}

        {!isLoading && filtered.length === 0 && (
          <div
            style={{
              gridColumn: '1/-1',
              textAlign: 'center',
              padding: 32,
              color: colors.textSub,
              border: `1px dashed ${colors.border}`,
              borderRadius: 24,
              fontSize: 14,
              letterSpacing: '.4px',
            }}
          >
            No badges match your filters.
          </div>
        )}
      </section>
    </main>
  );
}

/** Styles */
const colorTokens = (dark) =>
  dark
    ? {
        bgSoft: 'rgba(255,255,255,0.06)',
        border: 'rgba(255,255,255,0.09)',
        text: '#f1f5f9',
        textSub: '#cbd5e1',
        textSubFaint: '#94a3b8',
        accent: 'linear-gradient(90deg,#6366f1,#8b5cf6,#ec4899,#f59e0b)',
        chipBg: 'rgba(255,255,255,0.07)',
        shadow: '0 2px 6px rgba(0,0,0,0.55)',
      }
    : {
        bgSoft: '#f1f5f9',
        border: '#e2e8f0',
        text: '#1f2937',
        textSub: '#4b5563',
        textSubFaint: '#64748b',
        accent: 'linear-gradient(90deg,#6366f1,#8b5cf6,#ec4899,#f59e0b)',
        chipBg: '#ffffff',
        shadow: '0 2px 6px rgba(0,0,0,0.08)',
      };

const titleH1 = (dark) => ({
  margin: 0,
  fontSize: 26,
  fontWeight: 800,
  background: colorTokens(dark).accent,
  WebkitBackgroundClip: 'text',
  color: 'transparent',
  letterSpacing: '.6px',
  lineHeight: 1.1,
});

const squareImg = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};

const pillBtn = (colors) => ({
  border: `1px solid ${colors.border}`,
  borderRadius: 999,
  padding: '6px 16px',
  background: colors.chipBg,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '.35px',
  color: colors.text,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  backdropFilter: 'blur(4px)',
  transition:
    'background .3s ease, box-shadow .3s ease, transform .25s cubic-bezier(.4,.6,.3,1), border-color .3s',
});

const toggleBtn = (active, colors) => ({
  ...pillBtn(colors),
  background: active ? colors.bgSoft : colors.chipBg,
  boxShadow: active
    ? 'inset 0 0 0 1px rgba(99,102,241,.4),0 2px 4px rgba(0,0,0,.08)'
    : 'none',
});

const selectStyle = (colors) => ({
  border: `1px solid ${colors.border}`,
  borderRadius: 14,
  padding: '8px 14px 8px',
  background: colors.chipBg,
  fontSize: 12,
  color: colors.text,
  fontWeight: 500,
  letterSpacing: '.35px',
  outline: 'none',
});

const inputStyle = (colors) => ({
  border: `1px solid ${colors.border}`,
  borderRadius: 16,
  padding: '8px 14px',
  fontSize: 12,
  minWidth: 200,
  background: colors.chipBg,
  color: colors.text,
  outline: 'none',
  fontWeight: 500,
  letterSpacing: '.35px',
});

const badgeCard = (colors, dark) => ({
  position: 'relative',
  textAlign: 'center',
  border: `1px solid ${colors.border}`,
  background: colors.chipBg,
  borderRadius: 22,
  padding: '14px 12px 16px',
  cursor: 'default',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 188,
  outline: 'none',
  boxShadow: dark ? '0 2px 6px rgba(0,0,0,0.55)' : '0 2px 6px rgba(0,0,0,0.08)',
  transition:
    'box-shadow .38s cubic-bezier(.3,.7,.4,1), transform .38s cubic-bezier(.3,.7,.4,1), border-color .35s, background .35s',
  overflow: 'hidden',
});

const badgeImageWrap = (colors, dark) => ({
  width: 88,
  height: 88,
  borderRadius: '50%',
  margin: '0 auto 12px',
  background: colors.bgSoft,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  boxShadow: dark
    ? '0 0 0 1px rgba(255,255,255,0.09),0 6px 16px -4px rgba(0,0,0,0.65)'
    : '0 0 0 1px #e5e7eb,0 6px 16px -4px rgba(0,0,0,0.15)',
  transition: 'transform .4s cubic-bezier(.32,.72,.33,1)',
});

const badgeName = (colors) => ({
  fontWeight: 650,
  fontSize: 14,
  lineHeight: 1.3,
  color: colors.text,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  minHeight: 40,
  letterSpacing: '.3px',
  margin: '0 4px',
});

const badgeMeta = (colors) => ({
  fontSize: 11,
  color: colors.textSubFaint,
  marginTop: 8,
  letterSpacing: '.45px',
});

const progressPill = (colors) => ({
  position: 'absolute',
  right: 6,
  top: 6,
  background: colors.accent,
  WebkitBackgroundClip: 'text',
  color: 'transparent',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '.5px',
  textShadow:
    '0 0 10px rgba(99,102,241,0.35),0 0 4px rgba(236,72,153,0.35),0 0 2px rgba(245,158,11,0.55)',
  pointerEvents: 'none',
  userSelect: 'none',
});
